import { assertProjectAllowed, assertSkillAllowed, ProjectRegistryError, } from "../project-skill-registry/index.js";
import { DEFAULT_MAX_EVIDENCE_BYTES, DEFAULT_MAX_EVIDENCE_COUNT_PER_STAGE, DEFAULT_WORKFLOW_TIMEOUT_MS, getNextStageId, getStageDefinition, toCwjWorkflowStatus, } from "./constants.js";
import { CwjWorkflowEngineError } from "./types.js";
import { MemoryCwjWorkflowStore } from "./memoryWorkflowStore.js";
export { MemoryCwjWorkflowStore };
function isTerminalWorkflowStatus(status) {
    return status === "completed" || status === "failed" || status === "cancelled";
}
function createInitialStageStates() {
    const states = {};
    const stageIds = [
        "intake",
        "product_review",
        "engineering_plan",
        "design_security_review",
        "approval",
        "implementation",
        "code_review",
        "qa",
        "release_evidence",
        "owner_approval",
        "retrospective",
    ];
    for (const stageId of stageIds) {
        states[stageId] = {
            stageId,
            status: "pending",
            startedAt: undefined,
            completedAt: undefined,
            evidence: [],
            approvals: [],
            rejections: [],
            retryCount: 0,
            retryAttemptId: undefined,
            error: undefined,
            skippedBy: undefined,
            pausedAt: undefined,
            resumedAt: undefined,
            pauseCount: 0,
            pauseDurationMs: 0,
        };
    }
    return states;
}
export class CwjWorkflowEngine {
    constructor(options = {}) {
        this.emergencyStops = new Map();
        const now = () => Date.now();
        const gen = () => `wf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        this.options = {
            defaultTimeoutMs: options.defaultTimeoutMs ?? DEFAULT_WORKFLOW_TIMEOUT_MS,
            maxEvidenceBytes: options.maxEvidenceBytes ?? DEFAULT_MAX_EVIDENCE_BYTES,
            maxEvidenceCountPerStage: options.maxEvidenceCountPerStage ?? DEFAULT_MAX_EVIDENCE_COUNT_PER_STAGE,
            generateWorkflowId: options.generateWorkflowId ?? gen,
            clock: options.clock ?? now,
            store: options.store ?? new MemoryCwjWorkflowStore(),
            taskEngine: options.taskEngine,
        };
        this.store = this.options.store;
    }
    now() {
        return this.options.clock();
    }
    generateEventId() {
        return `wfevt-${this.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
    generateRetryAttemptId() {
        return `attempt-${this.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }
    generateWorkflowId(input) {
        return input.taskId ?? input.idempotencyKey ?? this.options.generateWorkflowId();
    }
    sameInput(a, b) {
        return (a.title === b.title &&
            a.description === b.description &&
            a.maxDurationMs === b.maxDurationMs &&
            JSON.stringify(a.payload) === JSON.stringify(b.payload));
    }
    getRelevantScopes(context) {
        const scopes = ["global"];
        if (context.projectId)
            scopes.push(`project:${context.projectId}`);
        if (context.skillId)
            scopes.push(`skill:${context.skillId}`);
        if (context.tenantId)
            scopes.push(`tenant:${context.tenantId}`);
        return scopes;
    }
    getCurrentEmergencyEpoch(context) {
        let epoch = 0;
        for (const scope of this.getRelevantScopes(context)) {
            const record = this.emergencyStops.get(scope);
            if (record && record.active && record.epoch > epoch) {
                epoch = record.epoch;
            }
        }
        return epoch;
    }
    async assertWorkflowScope(context, operation) {
        try {
            assertProjectAllowed(context.projectId, context.tenantId, context.ownerId, operation);
            assertSkillAllowed(context.skillId, context.projectId, context.tenantId, context.ownerId);
        }
        catch (err) {
            if (err instanceof ProjectRegistryError) {
                throw new CwjWorkflowEngineError(`invalid_scope: ${err.message}`, "invalid_scope");
            }
            throw err;
        }
    }
    assertActorAuthorized(context, actorId, action) {
        if (actorId === context.ownerId)
            return;
        if (actorId === context.tenantId)
            return;
        throw new CwjWorkflowEngineError(`actor_unauthorized: actor ${actorId} is not authorized to ${action} workflow owned by ${context.ownerId ?? "<none>"}`, "actor_unauthorized");
    }
    assertNotEmergencyStopped(workflow) {
        const epoch = this.getCurrentEmergencyEpoch(workflow.context);
        if (epoch > workflow.emergencyStopEpoch) {
            const matching = this.getRelevantScopes(workflow.context).find((s) => {
                const r = this.emergencyStops.get(s);
                return r?.active && r.epoch > workflow.emergencyStopEpoch;
            });
            if (matching) {
                const record = this.emergencyStops.get(matching);
                throw new CwjWorkflowEngineError(`emergency_stop: Emergency Stop active for ${matching}: ${record.reason ?? "no reason"}`, "emergency_stop");
            }
        }
    }
    assertWorkflowMutable(workflow) {
        if (isTerminalWorkflowStatus(workflow.status)) {
            throw new CwjWorkflowEngineError(`workflow_terminal: Workflow ${workflow.workflowId} is terminal (${workflow.status})`, "workflow_terminal");
        }
    }
    assertWorkflowNotPaused(workflow) {
        if (workflow.status === "paused") {
            throw new CwjWorkflowEngineError(`workflow_paused: Workflow ${workflow.workflowId} is paused`, "workflow_paused");
        }
    }
    assertWorkflowRunningOrPending(workflow) {
        if (workflow.status !== "running" && workflow.status !== "pending") {
            throw new CwjWorkflowEngineError(`workflow_not_running: Workflow ${workflow.workflowId} is ${workflow.status}`, "workflow_not_running");
        }
    }
    assertStageIsCurrent(workflow, stageId, action) {
        if (workflow.currentStageId !== stageId) {
            throw new CwjWorkflowEngineError(`not_current_stage: ${action} denied for ${stageId}; current stage is ${workflow.currentStageId}`, "not_current_stage");
        }
    }
    async assertNoTaskDrift(workflow) {
        if (!this.options.taskEngine)
            return;
        const task = await this.options.taskEngine.getTask(workflow.taskId);
        if (!task)
            return;
        if (task.context.projectId !== workflow.context.projectId ||
            task.context.skillId !== workflow.context.skillId ||
            task.context.tenantId !== workflow.context.tenantId ||
            task.context.ownerId !== workflow.context.ownerId) {
            throw new CwjWorkflowEngineError(`task_drift: bound task ${workflow.taskId} context does not match workflow context`, "task_drift");
        }
        const mapped = toCwjWorkflowStatus(task.status);
        if (!mapped) {
            throw new CwjWorkflowEngineError(`task_drift: bound task ${workflow.taskId} has unmapped status ${task.status}`, "task_drift");
        }
        const terminal = ["completed", "failed", "cancelled"];
        if (terminal.includes(mapped) && workflow.status !== mapped) {
            throw new CwjWorkflowEngineError(`task_drift: bound task ${workflow.taskId} is ${mapped} but workflow is ${workflow.status}`, "task_drift");
        }
        if (terminal.includes(workflow.status) &&
            mapped !== workflow.status &&
            !terminal.includes(mapped)) {
            throw new CwjWorkflowEngineError(`task_drift: workflow is ${workflow.status} but bound task ${workflow.taskId} is ${mapped}`, "task_drift");
        }
    }
    createAuditEvent(workflow, stageId, previousStageStatus, actorId, eventType, reason) {
        return {
            eventId: this.generateEventId(),
            eventType,
            workflowId: workflow.workflowId,
            stageId,
            previousStageStatus,
            actorId,
            reason,
            timestamp: this.now(),
        };
    }
    async save(workflow, expectedVersion) {
        await this.store.save(workflow, expectedVersion);
    }
    async requireWorkflow(workflowId) {
        const workflow = await this.store.load(workflowId);
        if (!workflow) {
            throw new CwjWorkflowEngineError(`Workflow not found: ${workflowId}`, "not_found");
        }
        return workflow;
    }
    mutate(workflow, stageId, actorId, eventType, mutator, reason) {
        const previousStageStatus = workflow.stageStates[stageId].status;
        const now = this.now();
        const next = {
            ...workflow,
            version: workflow.version + 1,
            updatedAt: now,
            auditEvents: [...workflow.auditEvents],
        };
        mutator(next);
        next.auditEvents.push(this.createAuditEvent(next, stageId, previousStageStatus, actorId, eventType, reason));
        return next;
    }
    isDangerousKey(key) {
        return /pass(word|wd)?|secret|token|api[-_]?key|bearer|credential|auth|private[-_]?key/i.test(key);
    }
    isExecutableString(value) {
        return /^#!/.test(value) || /\beval\s*\(|new\s+Function|Function\s*\(|child_process|os\.system|execSync|exec\s*\(/.test(value);
    }
    isBlobValue(value) {
        if (value === null || value === undefined)
            return false;
        if (typeof value === "object") {
            const ctor = value.constructor?.name;
            if (ctor === "Blob" ||
                ctor === "Buffer" ||
                ctor === "ArrayBuffer" ||
                ctor === "Uint8Array" ||
                ctor === "DataView") {
                return true;
            }
        }
        if (typeof value === "string") {
            return /^data:[^;]+;base64,/.test(value);
        }
        return false;
    }
    validateArtifactRef(ref) {
        if (!ref || typeof ref !== "string") {
            throw new CwjWorkflowEngineError("invalid_artifact_ref: artifact_ref must be a non-empty string", "invalid_artifact_ref");
        }
        if (ref.startsWith("/") || ref.startsWith("\\") || ref.includes("..") || ref.includes("\\")) {
            throw new CwjWorkflowEngineError(`invalid_artifact_ref: traversal not allowed: ${ref}`, "invalid_artifact_ref");
        }
        if (/^[A-Za-z]:/.test(ref) || ref.includes("://")) {
            throw new CwjWorkflowEngineError(`invalid_artifact_ref: must be a relative workspace path: ${ref}`, "invalid_artifact_ref");
        }
    }
    redactSecrets(value) {
        if (value === null || value === undefined)
            return value;
        if (typeof value === "string")
            return value;
        if (Array.isArray(value))
            return value.map((v) => this.redactSecrets(v));
        if (typeof value === "object") {
            const result = {};
            for (const [key, v] of Object.entries(value)) {
                if (this.isDangerousKey(key)) {
                    result[key] = "[REDACTED]";
                }
                else {
                    result[key] = this.redactSecrets(v);
                }
            }
            return result;
        }
        return value;
    }
    scanForDangerousPayload(value, path = "payload") {
        if (value === null || value === undefined)
            return;
        if (typeof value === "string") {
            if (this.isExecutableString(value)) {
                throw new CwjWorkflowEngineError(`executable/script content not allowed in ${path}`, "executable_payload");
            }
            if (/^data:[^;]+;base64,/.test(value)) {
                throw new CwjWorkflowEngineError(`raw blob / data URI not allowed in ${path}`, "raw_blob");
            }
            return;
        }
        if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                this.scanForDangerousPayload(value[i], `${path}[${i}]`);
            }
            return;
        }
        if (typeof value === "object") {
            const obj = value;
            for (const [key, v] of Object.entries(obj)) {
                if (/script|shell|command|exec|eval|system|subprocess|spawn|fork|child_process|os\.system|eval\s*\(/i.test(key)) {
                    throw new CwjWorkflowEngineError(`executable_payload: executable/script key not allowed in evidence: ${key}`, "executable_payload");
                }
                this.scanForDangerousPayload(v, `${path}.${key}`);
            }
            return;
        }
    }
    validateEvidencePayload(evidenceType, payload, idempotencyKey) {
        if (payload === null || payload === undefined) {
            throw new CwjWorkflowEngineError("invalid_evidence_payload: evidence payload is required", "invalid_evidence_payload");
        }
        if (this.isBlobValue(payload)) {
            throw new CwjWorkflowEngineError("raw_blob: raw blob evidence not allowed", "raw_blob");
        }
        if (typeof payload !== "object" || Array.isArray(payload) || payload === null) {
            throw new CwjWorkflowEngineError("invalid_evidence_payload: evidence payload must be a typed object with a 'type' field", "invalid_evidence_payload");
        }
        const raw = payload;
        const type = raw.type;
        if (typeof type !== "string") {
            throw new CwjWorkflowEngineError("invalid_evidence_payload: evidence payload must have a string 'type' field", "invalid_evidence_payload");
        }
        switch (type) {
            case "text": {
                if (typeof raw.text !== "string") {
                    throw new CwjWorkflowEngineError("invalid_evidence_payload: text evidence requires a string text field", "invalid_evidence_payload");
                }
                return { type: "text", text: raw.text };
            }
            case "artifact_ref": {
                if (typeof raw.ref !== "string") {
                    throw new CwjWorkflowEngineError("invalid_evidence_payload: artifact_ref evidence requires a string ref field", "invalid_evidence_payload");
                }
                this.validateArtifactRef(raw.ref);
                return { type: "artifact_ref", ref: raw.ref };
            }
            case "checklist": {
                if (!Array.isArray(raw.items)) {
                    throw new CwjWorkflowEngineError("invalid_evidence_payload: checklist evidence requires an items array", "invalid_evidence_payload");
                }
                const items = raw.items.map((item, i) => {
                    if (typeof item !== "object" ||
                        item === null ||
                        typeof item.label !== "string" ||
                        typeof item.passed !== "boolean") {
                        throw new CwjWorkflowEngineError(`invalid_evidence_payload: checklist item ${i} must have label and passed`, "invalid_evidence_payload");
                    }
                    return {
                        label: item.label,
                        passed: item.passed,
                    };
                });
                return { type: "checklist", items };
            }
            case "json": {
                this.scanForDangerousPayload(raw.value);
                return {
                    type: "json",
                    value: this.redactSecrets(raw.value),
                    idempotencyKey: idempotencyKey ?? (typeof raw.idempotencyKey === "string" ? raw.idempotencyKey : undefined),
                };
            }
            case "idempotency": {
                if (typeof raw.key !== "string" || !raw.key) {
                    throw new CwjWorkflowEngineError("invalid_evidence_payload: idempotency evidence requires a non-empty key", "invalid_evidence_payload");
                }
                return { type: "idempotency", key: raw.key };
            }
            default:
                throw new CwjWorkflowEngineError(`unknown evidence payload type for ${evidenceType}: ${type}`, "unknown_evidence_type");
        }
    }
    evidenceSizeBytes(payload) {
        return JSON.stringify(payload).length;
    }
    activeEvidenceCount(stage) {
        return stage.evidence.filter((e) => !e.superseded).length;
    }
    findEvidence(stage, evidenceType, idempotencyKey) {
        if (idempotencyKey === undefined)
            return undefined;
        return stage.evidence.find((e) => e.evidenceType === evidenceType &&
            !e.superseded &&
            e.idempotencyKey === idempotencyKey);
    }
    isStageEvidenceComplete(workflow, stageId) {
        const stageDef = getStageDefinition(stageId);
        const stage = workflow.stageStates[stageId];
        if (stageDef.requiresApproval) {
            return stage.approvals.length > 0;
        }
        if (stageDef.evidenceTypes.length === 0) {
            return stage.status === "running" || stage.status === "completed";
        }
        return stageDef.evidenceTypes.every((type) => stage.evidence.some((e) => e.evidenceType === type && !e.superseded));
    }
    async completeStageInternal(workflow, stageId, actorId) {
        const stage = workflow.stageStates[stageId];
        if (stage.status === "completed")
            return workflow;
        if (stage.status !== "running") {
            throw new CwjWorkflowEngineError(`stage_not_complete: Stage ${stageId} cannot be completed in status ${stage.status}`, "stage_not_complete");
        }
        const completed = this.mutate(workflow, stageId, actorId, "stage_completed", (w) => {
            w.stageStates[stageId].status = "completed";
            w.stageStates[stageId].completedAt = this.now();
        });
        await this.save(completed, workflow.version);
        return this.advanceWorkflowStage(completed, stageId, actorId);
    }
    async advanceWorkflowStage(workflow, completedStageId, actorId) {
        const nextStageId = getNextStageId(completedStageId);
        if (!nextStageId) {
            const final = this.mutate(workflow, completedStageId, actorId, "workflow_completed", (w) => {
                w.status = "completed";
                w.completedAt = this.now();
            });
            await this.save(final, workflow.version);
            return final;
        }
        const nextStage = workflow.stageStates[nextStageId];
        if (nextStage.status !== "pending") {
            throw new CwjWorkflowEngineError(`double_advance: stage ${nextStageId} is already ${nextStage.status}`, "double_advance");
        }
        const next = this.mutate(workflow, nextStageId, actorId, "stage_started", (w) => {
            w.currentStageId = nextStageId;
            w.stageStates[nextStageId].status = "running";
            w.stageStates[nextStageId].startedAt = this.now();
        });
        await this.save(next, workflow.version);
        return next;
    }
    setEmergencyStop(scope, active, reason) {
        const current = this.emergencyStops.get(scope) ?? { active: false, epoch: 0, reason: undefined };
        let epoch = current.epoch;
        if (active) {
            epoch += 1;
            this.options.taskEngine?.setEmergencyStop(scope, true, reason);
        }
        else {
            this.options.taskEngine?.setEmergencyStop(scope, false, reason);
        }
        this.emergencyStops.set(scope, { active, epoch, reason });
    }
    isEmergencyStopped(scope) {
        if (!scope)
            return false;
        return this.emergencyStops.get(scope)?.active ?? false;
    }
    async createWorkflow(context, input, actorId) {
        await this.assertWorkflowScope(context, "write");
        this.assertActorAuthorized(context, actorId, "create");
        const stopEpoch = this.getCurrentEmergencyEpoch(context);
        if (stopEpoch > 0) {
            throw new CwjWorkflowEngineError("emergency_stop: Emergency Stop active", "emergency_stop");
        }
        const workflowId = this.generateWorkflowId(input);
        const maxDurationMs = input.maxDurationMs ?? this.options.defaultTimeoutMs;
        const normalizedInput = { ...input, maxDurationMs };
        const existing = await this.store.load(workflowId);
        if (existing) {
            if (existing.context.projectId === context.projectId &&
                existing.context.skillId === context.skillId &&
                existing.context.tenantId === context.tenantId &&
                existing.context.ownerId === context.ownerId &&
                this.sameInput(existing.input, normalizedInput)) {
                return existing;
            }
            throw new CwjWorkflowEngineError(`idempotency_mismatch: Workflow ${workflowId} already exists with different context/input`, "idempotency_mismatch");
        }
        const now = this.now();
        const workflow = {
            workflowId,
            taskId: workflowId,
            context,
            input: normalizedInput,
            status: "pending",
            currentStageId: "intake",
            stageStates: createInitialStageStates(),
            version: 1,
            createdAt: now,
            updatedAt: now,
            startedAt: undefined,
            completedAt: undefined,
            emergencyStopEpoch: stopEpoch,
            auditEvents: [],
        };
        workflow.auditEvents.push(this.createAuditEvent(workflow, "intake", undefined, actorId, "workflow_created"));
        await this.save(workflow, 0);
        return workflow;
    }
    async getWorkflow(workflowId) {
        return this.store.load(workflowId);
    }
    async listWorkflows(filters) {
        return this.store.list(filters);
    }
    async startWorkflow(workflowId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "start");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        if (workflow.status !== "pending") {
            throw new CwjWorkflowEngineError(`Workflow ${workflowId} cannot be started in status ${workflow.status}`, "invalid_transition");
        }
        const next = this.mutate(workflow, "intake", actorId, "workflow_started", (w) => {
            w.status = "running";
            w.startedAt = this.now();
            w.stageStates.intake.status = "running";
            w.stageStates.intake.startedAt = this.now();
        });
        await this.save(next, workflow.version);
        return next;
    }
    async submitStageEvidence(workflowId, stageId, evidenceType, payload, actorId, options) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "submit evidence");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        this.assertWorkflowMutable(workflow);
        this.assertWorkflowNotPaused(workflow);
        this.assertWorkflowRunningOrPending(workflow);
        this.assertStageIsCurrent(workflow, stageId, "submitStageEvidence");
        const stageDef = getStageDefinition(stageId);
        const stage = workflow.stageStates[stageId];
        if (stage.status !== "pending" && stage.status !== "running" && stage.status !== "blocked") {
            throw new CwjWorkflowEngineError(`Stage ${stageId} cannot accept evidence in status ${stage.status}`, "invalid_stage");
        }
        if (!stageDef.evidenceTypes.includes(evidenceType)) {
            throw new CwjWorkflowEngineError(`invalid_evidence: Evidence type ${evidenceType} not required for stage ${stageId}`, "invalid_evidence");
        }
        const idempotencyKey = options?.idempotencyKey;
        const existing = this.findEvidence(stage, evidenceType, idempotencyKey);
        if (existing) {
            if (options?.supersede) {
                const validated = this.validateEvidencePayload(evidenceType, payload, idempotencyKey);
                const size = this.evidenceSizeBytes(validated);
                if (size > this.options.maxEvidenceBytes) {
                    throw new CwjWorkflowEngineError(`evidence_too_large: ${size} bytes exceeds ${this.options.maxEvidenceBytes}`, "evidence_too_large");
                }
                const newEvidenceId = this.generateEventId();
                const superseding = this.mutate(workflow, stageId, actorId, "evidence_superseded", (w) => {
                    const idx = w.stageStates[stageId].evidence.findIndex((e) => e.evidenceId === existing.evidenceId);
                    if (idx >= 0) {
                        w.stageStates[stageId].evidence[idx].superseded = true;
                        w.stageStates[stageId].evidence[idx].supersededBy = newEvidenceId;
                    }
                }, evidenceType);
                const newEvidence = {
                    evidenceId: newEvidenceId,
                    evidenceType,
                    payload: validated,
                    submittedBy: actorId,
                    submittedAt: this.now(),
                    idempotencyKey,
                    sizeBytes: size,
                    superseded: false,
                    supersededBy: undefined,
                };
                const withEvidence = this.mutate(superseding, stageId, actorId, "stage_evidence_submitted", (w) => {
                    w.stageStates[stageId].evidence.push(newEvidence);
                }, evidenceType);
                await this.save(withEvidence, workflow.version);
                if (this.isStageEvidenceComplete(withEvidence, stageId) && !stageDef.requiresApproval) {
                    return this.completeStageInternal(withEvidence, stageId, actorId);
                }
                return withEvidence;
            }
            if (idempotencyKey) {
                return workflow;
            }
            throw new CwjWorkflowEngineError(`evidence_duplicate: ${evidenceType} already submitted for stage ${stageId}`, "evidence_duplicate");
        }
        if (this.activeEvidenceCount(stage) >= this.options.maxEvidenceCountPerStage) {
            throw new CwjWorkflowEngineError(`evidence_limit_exceeded: stage ${stageId} has reached the maximum active evidence count`, "evidence_limit_exceeded");
        }
        const validated = this.validateEvidencePayload(evidenceType, payload, idempotencyKey);
        const size = this.evidenceSizeBytes(validated);
        if (size > this.options.maxEvidenceBytes) {
            throw new CwjWorkflowEngineError(`evidence_too_large: ${size} bytes exceeds ${this.options.maxEvidenceBytes}`, "evidence_too_large");
        }
        const evidence = {
            evidenceId: this.generateEventId(),
            evidenceType,
            payload: validated,
            submittedBy: actorId,
            submittedAt: this.now(),
            idempotencyKey,
            sizeBytes: size,
            superseded: false,
            supersededBy: undefined,
        };
        const next = this.mutate(workflow, stageId, actorId, "stage_evidence_submitted", (w) => {
            w.stageStates[stageId].evidence.push(evidence);
            if (w.stageStates[stageId].status === "pending") {
                w.stageStates[stageId].status = "running";
                w.stageStates[stageId].startedAt = this.now();
            }
        }, evidenceType);
        await this.save(next, workflow.version);
        if (this.isStageEvidenceComplete(next, stageId) && !stageDef.requiresApproval) {
            return this.completeStageInternal(next, stageId, actorId);
        }
        return next;
    }
    async approveStage(workflowId, stageId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "approve");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        this.assertWorkflowMutable(workflow);
        this.assertWorkflowNotPaused(workflow);
        this.assertWorkflowRunningOrPending(workflow);
        const stage = workflow.stageStates[stageId];
        if (stage.approvals.includes(actorId)) {
            throw new CwjWorkflowEngineError(`approval_duplicate: ${actorId} already approved stage ${stageId}`, "approval_duplicate");
        }
        this.assertStageIsCurrent(workflow, stageId, "approveStage");
        const stageDef = getStageDefinition(stageId);
        if (!stageDef.requiresApproval) {
            throw new CwjWorkflowEngineError(`approval_not_required: Stage ${stageId} does not require approval`, "approval_not_required");
        }
        this.assertApprover(workflow, stageDef.approverRole, actorId);
        const next = this.mutate(workflow, stageId, actorId, "stage_approved", (w) => {
            w.stageStates[stageId].approvals.push(actorId);
        });
        await this.save(next, workflow.version);
        if (this.isStageEvidenceComplete(next, stageId)) {
            return this.completeStageInternal(next, stageId, actorId);
        }
        return next;
    }
    assertApprover(workflow, role, actorId) {
        if (role === "none")
            return;
        if (role === "owner") {
            if (actorId !== workflow.context.ownerId) {
                throw new CwjWorkflowEngineError(`approval_denied: Owner approval required; ${actorId} is not owner`, "approval_denied");
            }
            return;
        }
        if (role === "tenant") {
            if (actorId !== workflow.context.tenantId && actorId !== workflow.context.ownerId) {
                throw new CwjWorkflowEngineError(`approval_denied: Tenant approval required; ${actorId} is not tenant/owner`, "approval_denied");
            }
        }
    }
    async rejectStage(workflowId, stageId, reason, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "reject");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        this.assertWorkflowMutable(workflow);
        this.assertWorkflowNotPaused(workflow);
        this.assertWorkflowRunningOrPending(workflow);
        this.assertStageIsCurrent(workflow, stageId, "rejectStage");
        const stageDef = getStageDefinition(stageId);
        const stage = workflow.stageStates[stageId];
        if (stage.status !== "running" && stage.status !== "blocked") {
            throw new CwjWorkflowEngineError(`Stage ${stageId} cannot be rejected in status ${stage.status}`, "invalid_stage");
        }
        if (stageDef.maxRetries > 0 && stage.retryCount < stageDef.maxRetries) {
            const retried = this.mutate(workflow, stageId, actorId, "stage_rejected_with_retry", (w) => {
                w.stageStates[stageId].status = "blocked";
                w.stageStates[stageId].rejections.push(actorId);
                w.stageStates[stageId].error = reason;
                w.stageStates[stageId].retryCount += 1;
                w.stageStates[stageId].retryAttemptId = this.generateRetryAttemptId();
            }, reason);
            await this.save(retried, workflow.version);
            return retried;
        }
        const failed = this.mutate(workflow, stageId, actorId, "stage_failed", (w) => {
            w.stageStates[stageId].status = "failed";
            w.stageStates[stageId].error = reason;
            w.stageStates[stageId].rejections.push(actorId);
            w.stageStates[stageId].retryCount += 1;
            w.status = "failed";
        }, reason);
        await this.save(failed, workflow.version);
        return failed;
    }
    async retryStage(workflowId, stageId, actorId, force = false) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "retry");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        this.assertWorkflowMutable(workflow);
        this.assertWorkflowNotPaused(workflow);
        this.assertStageIsCurrent(workflow, stageId, "retryStage");
        const stage = workflow.stageStates[stageId];
        if (stage.status !== "blocked" && stage.status !== "failed") {
            throw new CwjWorkflowEngineError(`Stage ${stageId} cannot be retried in status ${stage.status}`, "invalid_transition");
        }
        const stageDef = getStageDefinition(stageId);
        if (stage.retryCount > stageDef.maxRetries && !force) {
            throw new CwjWorkflowEngineError(`max_retries_exceeded: stage ${stageId} has reached retry limit ${stageDef.maxRetries}`, "max_retries_exceeded");
        }
        const next = this.mutate(workflow, stageId, actorId, "stage_retried", (w) => {
            w.stageStates[stageId].status = "running";
            w.stageStates[stageId].error = undefined;
            w.stageStates[stageId].rejections = [];
            w.stageStates[stageId].startedAt = this.now();
            w.stageStates[stageId].retryAttemptId = this.generateRetryAttemptId();
        });
        await this.save(next, workflow.version);
        return next;
    }
    async skipStage(workflowId, stageId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "skip");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        this.assertWorkflowMutable(workflow);
        this.assertWorkflowNotPaused(workflow);
        this.assertStageIsCurrent(workflow, stageId, "skipStage");
        const stageDef = getStageDefinition(stageId);
        if (!stageDef.skipAllowed) {
            throw new CwjWorkflowEngineError(`skip_denied: Stage ${stageId} is mandatory and cannot be skipped`, "skip_denied");
        }
        const stage = workflow.stageStates[stageId];
        if (stage.status !== "pending" && stage.status !== "running") {
            throw new CwjWorkflowEngineError(`Stage ${stageId} cannot be skipped in status ${stage.status}`, "invalid_transition");
        }
        const next = this.mutate(workflow, stageId, actorId, "stage_skipped", (w) => {
            w.stageStates[stageId].status = "skipped";
            w.stageStates[stageId].skippedBy = actorId;
            w.stageStates[stageId].completedAt = this.now();
        });
        await this.save(next, workflow.version);
        return this.advanceWorkflowStage(next, stageId, actorId);
    }
    async advanceStage(workflowId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "advance");
        this.assertNotEmergencyStopped(workflow);
        await this.assertNoTaskDrift(workflow);
        this.assertWorkflowMutable(workflow);
        this.assertWorkflowNotPaused(workflow);
        const currentStageId = workflow.currentStageId;
        const stage = workflow.stageStates[currentStageId];
        if (stage.status !== "completed") {
            throw new CwjWorkflowEngineError(`stage_not_complete: Current stage ${currentStageId} is not completed`, "stage_not_complete");
        }
        const next = getNextStageId(currentStageId);
        if (!next) {
            const completed = this.mutate(workflow, currentStageId, actorId, "workflow_completed", (w) => {
                w.status = "completed";
                w.completedAt = this.now();
            });
            await this.save(completed, workflow.version);
            return completed;
        }
        const nextStage = workflow.stageStates[next];
        if (nextStage.status !== "pending") {
            throw new CwjWorkflowEngineError(`double_advance: stage ${next} is already ${nextStage.status}`, "double_advance");
        }
        const advanced = this.mutate(workflow, next, actorId, "stage_started", (w) => {
            w.currentStageId = next;
            w.stageStates[next].status = "running";
            w.stageStates[next].startedAt = this.now();
        });
        await this.save(advanced, workflow.version);
        return advanced;
    }
    async pauseWorkflow(workflowId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "pause");
        this.assertNotEmergencyStopped(workflow);
        if (workflow.status === "paused")
            return workflow;
        if (workflow.status !== "running") {
            throw new CwjWorkflowEngineError(`Workflow ${workflowId} cannot be paused in status ${workflow.status}`, "invalid_transition");
        }
        const stageId = workflow.currentStageId;
        const next = this.mutate(workflow, stageId, actorId, "workflow_paused", (w) => {
            w.status = "paused";
            w.stageStates[stageId].pausedAt = this.now();
            w.stageStates[stageId].pauseCount += 1;
        });
        await this.save(next, workflow.version);
        return next;
    }
    async resumeWorkflow(workflowId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "resume");
        this.assertNotEmergencyStopped(workflow);
        if (workflow.status === "running")
            return workflow;
        if (workflow.status !== "paused") {
            throw new CwjWorkflowEngineError(`Workflow ${workflowId} cannot be resumed in status ${workflow.status}`, "invalid_transition");
        }
        const stageId = workflow.currentStageId;
        const next = this.mutate(workflow, stageId, actorId, "workflow_resumed", (w) => {
            w.status = "running";
            if (w.stageStates[stageId].pausedAt) {
                w.stageStates[stageId].pauseDurationMs += this.now() - w.stageStates[stageId].pausedAt;
                w.stageStates[stageId].pausedAt = undefined;
            }
            w.stageStates[stageId].resumedAt = this.now();
        });
        await this.save(next, workflow.version);
        return next;
    }
    async cancelWorkflow(workflowId, actorId) {
        const workflow = await this.requireWorkflow(workflowId);
        await this.assertWorkflowScope(workflow.context, "write");
        this.assertActorAuthorized(workflow.context, actorId, "cancel");
        if (workflow.status === "cancelled")
            return workflow;
        if (isTerminalWorkflowStatus(workflow.status)) {
            throw new CwjWorkflowEngineError(`Workflow ${workflowId} is already terminal (${workflow.status})`, "invalid_transition");
        }
        const stageId = workflow.currentStageId;
        const next = this.mutate(workflow, stageId, actorId, "workflow_cancelled", (w) => {
            w.status = "cancelled";
            w.completedAt = this.now();
        });
        await this.save(next, workflow.version);
        return next;
    }
    async checkTimeout(workflowId) {
        const workflow = await this.requireWorkflow(workflowId);
        if (isTerminalWorkflowStatus(workflow.status))
            return workflow;
        if (workflow.status === "paused")
            return workflow;
        const currentStageId = workflow.currentStageId;
        const stage = workflow.stageStates[currentStageId];
        const stageDef = getStageDefinition(currentStageId);
        const startedAt = stage.startedAt ?? workflow.startedAt ?? workflow.createdAt;
        const timeoutMs = workflow.input.maxDurationMs ?? stageDef.timeoutMs;
        const elapsed = this.now() - startedAt - stage.pauseDurationMs;
        if (elapsed > timeoutMs) {
            const timedOut = this.mutate(workflow, currentStageId, "system", "stage_timeout", (w) => {
                w.stageStates[currentStageId].status = "failed";
                w.stageStates[currentStageId].error = `Stage ${currentStageId} timed out`;
                w.status = "failed";
            }, "timeout");
            await this.save(timedOut, workflow.version);
            return timedOut;
        }
        return workflow;
    }
}
//# sourceMappingURL=workflowEngine.js.map